import { Button, Col, message, Row, Select, Slider } from "antd";
import { EditFilled } from "@ant-design/icons";
import PaddleCheckout from "./PaddleCheckout";
import { useDispatch } from "react-redux";
import { useCallback, useState } from "react";
import { getSubscriptionDetails } from "../helper";
import { parseError } from "../../../../utils/shared";
import httpClient from "../../../../api/httpClient";
import { setKeyValue } from "../../../../store/user/slice";

const CostEstimator = ({
  selectedPlanId,
  currencySymbol,
  pricingDetails,
  teamMembers,
  setSelectedPlanId,
  plans,
  setTeamMembers,
  selectedPlan,
  perMemberPrice,
  closeModal,
  isCanceled,
  currentPlan,
}) => {
  const [submitting, setSubmitting] = useState(false);

  const dispatch = useDispatch();

  const onUpgrade = useCallback(async () => {
    try {
      setSubmitting(true);

      const { perSeat, selectedPlan: currentSelectedPlan } =
        getSubscriptionDetails(currentPlan);

      if (
        currentSelectedPlan?.price?.id === selectedPlan?.price?.id &&
        perSeat?.quantity === teamMembers
      )
        throw new Error("No updates were made to your subscription");

      const {
        data,
        status,
        //@ts-ignore
        message: apiMessage,
      } = await httpClient.post("paddle/update-subscription", [
        {
          price_id: selectedPlan?.price?.id,
          quantity: 1,
        },
        ...(teamMembers > 0
          ? [
              {
                price_id: perMemberPrice?.price?.id,
                quantity: teamMembers,
              },
            ]
          : []),
      ]);

      if (!status)
        throw new Error(
          apiMessage?.error?.detail || "Update subscription failed",
        );

      dispatch(
        setKeyValue({
          key: "currentPlan",
          value: data?.data,
        }),
      );

      message.success("Subscription updated successfully");

      closeModal();
    } catch (error) {
      parseError(error, true);
    } finally {
      setSubmitting(false);
    }
  }, [currentPlan, selectedPlan, teamMembers, perMemberPrice]);

  return (
    <div className="estimator-section">
      <div className="estimator-title">📊 Estimate your monthly cost</div>

      <Row gutter={[32, 32]}>
        <Col xs={24} md={12}>
          <div className="estimator-field">
            <span className="field-label">Selected Plan</span>
            <Select
              value={selectedPlanId}
              onChange={setSelectedPlanId}
              style={{ width: "100%" }}
              options={plans.map((plan) => ({
                label: plan.name,
                value: plan.id,
              }))}
            />
          </div>
        </Col>

        <Col xs={24} md={12}>
          <div className="estimator-field">
            <span className="field-label">Number of Team Members</span>
            <div className="team-members-slider">
              <Slider
                min={0}
                max={50}
                value={teamMembers}
                onChange={setTeamMembers}
                marks={{
                  0: "0",
                  10: "10",
                  20: "20",
                  30: "30",
                  40: "40",
                  50: "50+",
                }}
              />
            </div>
          </div>
        </Col>
      </Row>

      {/* Cost Breakdown */}
      <div className="cost-breakdown">
        <div className="breakdown-row">
          <div className="breakdown-label">
            <span className="label-main">Base Plan</span>
            <span className="label-sub">{selectedPlan?.name}</span>
          </div>
          <span className="breakdown-value">
            {currencySymbol}
            {pricingDetails.basePlanAmount.toFixed(2)}
          </span>
        </div>

        <div className="breakdown-row">
          <div className="breakdown-label">
            <span className="label-main">Team Members</span>
            <span className="label-sub">
              {teamMembers} × {currencySymbol}
              {pricingDetails.memberAmount.toFixed(2)}
            </span>
          </div>
          <span className="breakdown-value">
            {currencySymbol}
            {pricingDetails.totalMembersCost.toFixed(2)}
          </span>
        </div>

        <div className="breakdown-row total-row">
          <span className="label-main">Total Monthly Cost</span>
          <span className="breakdown-value">
            {currencySymbol}
            {pricingDetails.monthlyCost.toFixed(2)}
          </span>
        </div>
      </div>
      {closeModal && !isCanceled ? (
        <div className="w-100 flex justify-end" onClick={onUpgrade}>
          <Button
            icon={<EditFilled />}
            type="primary"
            disabled={submitting}
            loading={submitting}
          >
            Update Plan
          </Button>
        </div>
      ) : (
        <PaddleCheckout
          selectedPlan={selectedPlan}
          teamMembers={teamMembers}
          perMemberPrice={perMemberPrice}
          closeModal={closeModal}
        />
      )}
    </div>
  );
};

export default CostEstimator;
